/**
 * Light and dark, chosen by the person rather than by the machine.
 *
 * The operating system's preference is the starting point, not the verdict: the
 * same workstation is used in a bright room in the morning and a dark workshop
 * in the evening, and Windows does not switch for that. So three states — follow
 * the system, force light, force dark — and the choice outlives the tab.
 *
 * Kept outside React because it has to run before the first paint: a dark-theme
 * user who sees a white page for one frame every time they open the app notices
 * it, and no amount of styling later makes up for it.
 */

import { isNative } from './host';

export type ThemePreference = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

const STORAGE_KEY = 'storageapp.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function readStored(): ThemePreference {
  try {
    const found = localStorage.getItem(STORAGE_KEY);
    return found === 'light' || found === 'dark' || found === 'system' ? found : 'system';
  } catch {
    // Private mode, or storage disabled by policy. Following the system is the
    // one answer that is never wrong, so a missing preference is not an error.
    return 'system';
  }
}

let preference: ThemePreference = readStored();
let theme: Theme = 'light';

const listeners = new Set<() => void>();

function systemTheme(): Theme {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * The colour the browser paints around the page: the address bar on Android,
 * the title bar of an installed app. Taken from the stylesheet rather than
 * repeated here, so that retuning the palette cannot leave this behind.
 */
function paintBrowserChrome(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) return;
  const colour = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  if (colour) meta.content = colour;
}

/**
 * The status bar above the application, on Android.
 *
 * `<meta name="theme-color">` is what a browser reads; a native status bar is
 * not painted by the page at all and has to be told separately, or the clock
 * and the battery stay black on a black bar in the dark theme.
 *
 * Loaded on demand so that the browser build never carries the plugin.
 */
function paintStatusBar(): void {
  if (!isNative) return;
  const colour = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  void import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
    // `Style.Dark` means dark *content* on Android's naming, which is what a
    // light background wants — the one place this reads backwards.
    void StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light });
    if (colour.startsWith('#')) void StatusBar.setBackgroundColor({ color: colour });
  });
}

function apply(): void {
  theme = preference === 'system' ? systemTheme() : preference;
  const root = document.documentElement;
  root.dataset.theme = theme;
  // Native widgets — scrollbars, date pickers, the caret — are painted by the
  // browser and only follow this property, not our variables.
  root.style.colorScheme = theme;
  paintBrowserChrome();
  paintStatusBar();
}

/**
 * Install the theme. Call once, as early as possible, before rendering.
 */
export function startTheming(): void {
  apply();
  window.matchMedia?.(DARK_QUERY).addEventListener('change', () => {
    if (preference !== 'system') return;
    apply();
    listeners.forEach((notify) => notify());
  });
}

export function getPreference(): ThemePreference {
  return preference;
}

export function getTheme(): Theme {
  return theme;
}

export function setPreference(next: ThemePreference): void {
  preference = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // The choice still applies to this session; it just will not be remembered.
  }
  apply();
  listeners.forEach((notify) => notify());
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
